import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { FormError } from '../components/AuthLayout';
import { useAuth } from '../contexts/AuthContext';

import { API } from '../lib/api';

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { refreshUser } = useAuth();
  const [state, setState] = useState('checking'); // checking | active | pending | error

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/billing/confirm`, { session_id: sessionId });
        await refreshUser();
        setState(data.is_pro ? 'active' : 'pending');
      } catch {
        setState('error');
      }
    })();
  }, [sessionId, refreshUser]);

  return (
    <DashboardLayout>
      <div className="mx-auto flex max-w-[560px] flex-col items-start gap-4 rounded-md border border-line bg-surface p-8" data-testid="billing-success">
        {state === 'checking' && (
          <p className="m-0 inline-flex items-center gap-2 text-ink-muted" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />Confirming your payment…
          </p>
        )}
        {state === 'active' && (
          <>
            <ShieldCheck className="h-8 w-8 text-sev-low" strokeWidth={1.5} aria-hidden="true" />
            <h1 className="m-0 font-display text-3xl font-bold leading-9">You have Pilot Pro</h1>
            <p className="m-0 text-ink-muted">PDF reports and move-in/move-out comparison are unlocked.</p>
            <Button asChild><Link to="/dashboard">Go to dashboard</Link></Button>
          </>
        )}
        {state === 'pending' && (
          <>
            <h1 className="m-0 font-display text-3xl font-bold leading-9">Payment received</h1>
            <p className="m-0 text-ink-muted">Stripe is still confirming it. Pro switches on within a minute; refresh this page to check.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 className="m-0 font-display text-3xl font-bold leading-9">We couldn't confirm this payment</h1>
            <FormError>If you were charged, Pro will switch on shortly. Contact us if it doesn't within a few minutes.</FormError>
            <Button asChild variant="outline"><Link to="/upgrade">Back to Pilot Pro</Link></Button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
